import { css } from 'lit'

const inputGlobalStyles = css`
  /* ============================================
     INPUT & FORM STYLES
     ============================================ */

  /* Base input styles */
  input:not([type='checkbox']):not([type='radio']),
  select,
  textarea {
    width: 100%;
    padding: 0.625rem 0.75rem;  /* 10px 12px */
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;  /* 6px */
    font-size: 0.875rem;  /* 14px */
    font-family: var(--font-family, inherit);
    line-height: 1.5;
    color: var(--text-primary, #1f2937);
    background: white;
    box-sizing: border-box;
    transition: all 0.2s ease;
  }

  /* Focus state */
  input:not([type='checkbox']):not([type='radio']):focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  /* Hover state */
  input:not([type='checkbox']):not([type='radio']):hover:not(:focus):not(:disabled),
  select:hover:not(:focus):not(:disabled),
  textarea:hover:not(:focus):not(:disabled) {
    border-color: #9ca3af;
  }

  /* Disabled state */
  input:disabled,
  select:disabled,
  textarea:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Error/Invalid state */
  input[aria-invalid="true"],
  input[invalid],
  select[aria-invalid="true"],
  textarea[aria-invalid="true"] {
    border-color: var(--text-error, #ef4444);
    background: #fef2f2;
  }

  input[aria-invalid="true"]:focus,
  input[invalid]:focus,
  select[aria-invalid="true"]:focus,
  textarea[aria-invalid="true"]:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  /* Placeholder styles */
  input::placeholder,
  textarea::placeholder {
    color: #9ca3af;
  }

  /* Textarea specific */
  textarea {
    min-height: 6rem;
    resize: vertical;
  }

  /* Select specific */
  select {
    cursor: pointer;
    padding-right: 2rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    appearance: none;
  }

  /* Checkbox and radio styles */
  input[type="checkbox"],
  input[type="radio"] {
    width: 1.125rem;  /* 18px */
    height: 1.125rem;
    cursor: pointer;
    border: 1px solid #d1d5db;
    transition: all 0.15s ease;
  }

  input[type="checkbox"] {
    border-radius: 0.25rem;
  }

  input[type="radio"] {
    border-radius: 50%;
  }

  input[type="checkbox"]:checked,
  input[type="radio"]:checked {
    background-color: #3b82f6;
    border-color: #3b82f6;
  }

  input[type="checkbox"]:focus,
  input[type="radio"]:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  /* Label styles */
  label {
    display: block;
    margin-bottom: 0.375rem;  /* 6px */
    font-size: 0.875rem;  /* 14px */
    font-weight: 500;
    color: #4b5563;
  }

  /* Input sizes */
  .input-sm {
    padding: 0.375rem 0.5rem;  /* 6px 8px */
    font-size: 0.75rem;  /* 12px */
  }

  .input-lg {
    padding: 0.75rem 1rem;  /* 12px 16px */
    font-size: 1rem;  /* 16px */
  }

  /* Form layout helpers */
  .form-group {
    margin-bottom: 1rem;
  }

  .form-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .form-row > * {
    flex: 1;
  }

  /* Input with prefix/suffix */
  .input-with-prefix,
  .input-with-suffix {
    position: relative;
    display: flex;
    align-items: center;
  }

  .input-with-prefix .prefix,
  .input-with-suffix .suffix {
    position: absolute;
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 500;
    pointer-events: none;
  }

  .input-with-prefix .prefix {
    left: 0.75rem;
  }

  .input-with-suffix .suffix {
    right: 0.75rem;
  }

  .input-with-prefix input {
    padding-left: 1.75rem;
  }

  .input-with-suffix input {
    padding-right: 1.75rem;
  }

  /* Helper text */
  .help-text {
    display: block;
    margin-top: 0.375rem;
    font-size: 0.75rem;  /* 12px */
    color: #6b7280;
  }

  .help-text.error {
    color: var(--text-error, #ef4444);
  }

  /* Search input styling */
  input[type="search"] {
    padding-right: 2rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3e%3c/svg%3e");
    background-position: right 0.75rem center;
    background-repeat: no-repeat;
    background-size: 1.25rem;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .form-row {
      flex-direction: column;
      gap: 0;
    }

    .form-row .form-group {
      margin-bottom: 1rem;
    }
  }
`

export default inputGlobalStyles
